/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    DividerNew as Divider,
    FlexRow,
    FlexCol,
    Icon,
    Stack,
    Text,
    TextBlock,
    HtmlParser,
} from 'sarsaparilla';
import get from 'lodash/get';
import {
    feeDisplayModels,
    formatCurrency,
    convertLineItemsToFeesMap,
    calculateRemainingIssuanceFee,
    hasRule,
} from '../../utils/utilsNew';
import FeeBreakdownByDateRange from './FeeBreakdownByDateRange';
import InfoModal from '../../../shared-components/common/InfoModal';
import * as types from '../../constants';

export default class FeeEstimate extends React.Component {
    static propTypes = {
        permit: PropTypes.shape({
            components: PropTypes.shape({
                fee_by_date_range: PropTypes.bool,
            }),
        }),
        lineItems: PropTypes.arrayOf(
            PropTypes.shape({
                Description: PropTypes.string,
                FeeType: PropTypes.number,
                Quantity: PropTypes.number,
                Sku: PropTypes.string,
                UnitPrice: PropTypes.number,
            })
        ),
        isLottery: PropTypes.bool,
        groupMembers: PropTypes.arrayOf(PropTypes.object),
        fees: PropTypes.array.isRequired,
        feeDisplayModel: PropTypes.number.isRequired,
        amountPaid: PropTypes.number.isRequired,
        feeCalculationModel: PropTypes.number.isRequired,
        isHighUseFeeApplicable: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        permit: { components: { fee_by_date_range: false } },
        isLottery: false,
        groupMembers: [],
        lineItems: [],
        feeCalculationModel: -1,
        feeDisplayModel: -1,
        amountPaid: 0,
    };

    constructor(props) {
        super(props);
        this.renderAdditionalFees = this.renderAdditionalFees.bind(this);
        this.renderUseFees = this.renderUseFees.bind(this);
        this.openFeesModal = this.openFeesModal.bind(this);
        this.closeFeesModal = this.closeFeesModal.bind(this);
        this.state = {
            feesVisible: false,
        };
    }

    openFeesModal() {
        this.setState({ feesVisible: true });
    }

    closeFeesModal() {
        this.setState({ feesVisible: false });
    }

    renderTwoColumnFee(col1, col2) {
        return (
            <FlexRow data-testid="two-column-fee">
                {col1}
                {col2}
            </FlexRow>
        );
    }

    renderTwoColumnFeeText(col1, col2) {
        return this.renderTwoColumnFee(
            <FlexCol xs={8} className="rec-price-label">
                {col1}
            </FlexCol>,
            <FlexCol xs={4} className="rec-price-value">
                <TextBlock textAlign="right">{col2}</TextBlock>
            </FlexCol>
        );
    }

    renderTwoColumnFeeTotal(col2, feePolicy) {
        return this.renderTwoColumnFeeText(
            <Text fontWeight="semibold">
                Total Cost
                {!!feePolicy && (
                    <Button
                        type="button"
                        onClick={this.openFeesModal}
                        iconBeforeElement={<Icon iconName="info" />}
                        screenReaderTextBefore="Open modal for more information about how the total cost is calculated"
                        appearance="link"
                        size="xs"
                    />
                )}
            </Text>,
            <Text fontWeight="semibold">{col2}</Text>
        );
    }

    renderAdditionalFees(feesMap) {
        const residualFeeLabel = this.props.isLottery
            ? 'Lottery Application Fee'
            : 'Reservation Fee';
        const residualFee = feesMap.get(types.RESERVATION_FEE);
        return (
            <>
                {feesMap.get(types.ATTRIBUTE_FEE) > 0 &&
                    this.renderTwoColumnFeeText(
                        feesMap.get(types.ATTRIBUTE_FEE_DESCRIPTION),
                        formatCurrency(feesMap.get(types.ATTRIBUTE_FEE))
                    )}
                {feesMap.get(types.NONREFUNDABLE_FEE) > 0 &&
                    this.renderTwoColumnFeeText(
                        types.NONREFUNDABLE_LABEL,
                        formatCurrency(feesMap.get(types.NONREFUNDABLE_FEE))
                    )}
                {this.renderTwoColumnFeeText(
                    residualFeeLabel,
                    formatCurrency(residualFee)
                )}
            </>
        );
    }

    renderUseFees(feesMap) {
        if (this.props.permit.components.fee_by_date_range) {
            // send guides to fee breakdown if it counts towards group size
            const guidesCountTowardsGroupSize = hasRule(
                types.GUIDES_COUNT_TOWARDS_GROUP_SIZE,
                get(this.props, 'permit.rules')
            );
            const quotaByMemberDaily = hasRule(
                types.MEMBER_AVAILABILITY_DAILY_KEY_NAME,
                get(this.props, 'permit.rules'),
                get(this.props, 'division.id')
            );
            let guidesAreCharged = false;
            for (let i = 0; i < this.props.fees.length; i++) {
                if (this.props.fees[i].is_guide_fee) {
                    guidesAreCharged = true;
                }
            }
            return (
                <FeeBreakdownByDateRange
                    groupMembers={this.props.groupMembers}
                    entryDate={this.props.entryDate}
                    exitDate={this.props.exitDate}
                    guides={
                        guidesCountTowardsGroupSize &&
                        guidesAreCharged &&
                        quotaByMemberDaily
                            ? this.props.guides
                            : []
                    }
                    fees={this.props.fees}
                    feeCalculationModel={this.props.feeCalculationModel}
                    isHighUseFeeApplicable={this.props.isHighUseFeeApplicable}
                    lineItems={this.props.lineItems}
                    permit={this.props.permit}
                />
            );
        }
        return this.renderTwoColumnFeeText(
            'Use Fee',
            formatCurrency(feesMap.get(types.USE_FEE))
        );
    }

    render() {
        const lineItems = this.props.lineItems;
        const feesMap = convertLineItemsToFeesMap(lineItems);
        const permitName = get(this.props.permit, 'name');
        const feePolicy = get(this.props.permit, 'description.fee_policy');
        const totalCost = calculateRemainingIssuanceFee(
            this.props.lineItems,
            this.props.amountPaid
        );

        // fee should be rolled up as a single total cost (one fee)
        if (this.props.feeDisplayModel === feeDisplayModels.get(types.ONE_FEE)) {
            return (
                <>
                    <Stack space="sm">
                        {this.props.amountPaid > 0 &&
                            this.renderTwoColumnFeeText(
                                'Amount Paid',
                                formatCurrency(this.props.amountPaid)
                            )}
                    </Stack>
                    <Divider />
                    {this.renderTwoColumnFeeTotal(formatCurrency(totalCost), feePolicy)}
                    {feePolicy && (
                        <InfoModal
                            isOpen={this.state.feesVisible}
                            closeModal={this.closeFeesModal}
                            title={permitName && `Fees for the ${permitName}`}
                        >
                            <div>
                                <HtmlParser html={feePolicy} />
                            </div>
                        </InfoModal>
                    )}
                </>
            );
        }

        // fee should show breakdown per categories (add-on fee)
        if (this.props.feeDisplayModel === feeDisplayModels.get(types.ADD_ON_FEE)) {
            return (
                <>
                    <Stack space="sm">
                        {!this.props.isLottery && this.renderUseFees(feesMap)}
                        {this.renderAdditionalFees(feesMap)}
                        {this.props.amountPaid > 0 &&
                            this.renderTwoColumnFeeText(
                                'Amount Paid',
                                formatCurrency(this.props.amountPaid)
                            )}
                    </Stack>
                    <Divider />
                    {this.renderTwoColumnFeeTotal(formatCurrency(totalCost), feePolicy)}
                    {feePolicy && (
                        <InfoModal
                            isOpen={this.state.feesVisible}
                            closeModal={this.closeFeesModal}
                            title={permitName && `Fees for the ${permitName}`}
                        >
                            <div>
                                <HtmlParser html={feePolicy} />
                            </div>
                        </InfoModal>
                    )}
                </>
            );
        }

        // fee should show breakdown by days?
        if (this.props.feeDisplayModel === feeDisplayModels.get(types.BY_DAY_FEE)) {
            return (
                <>
                    <Stack space="sm">
                        {this.renderAdditionalFees(feesMap)}
                        {this.props.amountPaid > 0 &&
                            this.renderTwoColumnFeeText(
                                'Amount Paid',
                                formatCurrency(this.props.amountPaid)
                            )}
                    </Stack>
                    <Divider />
                    {this.renderTwoColumnFeeTotal(formatCurrency(totalCost), feePolicy)}
                    {feePolicy && (
                        <InfoModal
                            isOpen={this.state.feesVisible}
                            closeModal={this.closeFeesModal}
                            title={permitName && `Fees for the ${permitName}`}
                        >
                            <div>
                                <HtmlParser html={feePolicy} />
                            </div>
                        </InfoModal>
                    )}
                </>
            );
        }
        return null;
    }
}
